import { brandedQrImage } from './brandedQr';
import { brandPath } from './brand';

// Print-ready poster sizes at 300 DPI (Phase 12).
// Docs: docs/plans/2026-08-ui-refresh/14-qr-codes-and-share-posters.md
export const POSTER_SIZES = {
  poster: { w: 2550, h: 3300, label: 'Poster · 8.5×11"' },
  repcard: { w: 1200, h: 1800, label: 'Rep card · 4×6"' },
  business: { w: 1050, h: 600, label: 'Business card · 3.5×2"' },
};

/**
 * The logo is drawn geometry, never type (docs/plans/2026-09-portal-logo). These sheets go to a printer,
 * so they take the flat composite: the sanctioned form for a surface we don't render live. The word is
 * #000 or #FFF, nothing else.
 */
const LOCKUP_SRC = { white: '/brand/portal-lockup-white.png', black: '/brand/portal-lockup-black.png' };
// The composite pads the word on every side (the portal's tails): 208px of 3416.
const LOCKUP_PAD = 208 / 3416;
const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  // Never let a silent loader stall the sheet: environments that fire neither event
  // (jsdom, a blocked asset) would otherwise hang generateSharePoster forever.
  const bail = setTimeout(() => reject(new Error('image timed out')), 3000);
  img.onload = () => { clearTimeout(bail); resolve(img); };
  img.onerror = () => { clearTimeout(bail); reject(new Error('image unavailable')); };
  img.src = src;
});

/** Draw the lockup by its WORD: a 6.25 × cap wide word whose capitals start at capTop. */
function drawLockup(ctx, img, x, capTop, cap) {
  const width = 6.25 * cap;
  const imgW = width / (1 - 2 * LOCKUP_PAD);
  const imgH = imgW * (img.height / img.width);
  const pad = imgW * LOCKUP_PAD;
  ctx.drawImage(img, Math.round(x - pad), Math.round(capTop - pad), Math.round(imgW), Math.round(imgH));
}

const PARTY = ['#FD199F', '#FF33C6', '#B5B1FF', '#127BFD', '#22ECFB', '#FAE1B4', '#FCAA6D'];
function partyGradient(ctx, x0, y0, x1, y1) {
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  PARTY.forEach((c, i) => g.addColorStop(i / (PARTY.length - 1), c));
  return g;
}

const containIn = (iw, ih, bx, by, bw, bh) => {
  const s = Math.min(bw / iw, bh / ih);
  const [w, h] = [iw * s, ih * s];
  return { x: bx + (bw - w) / 2, y: by + (bh - h) / 2, w, h };
};

/**
 * The poster, cover at 120% and blurred, inside a band. iOS's canvas has no ctx.filter, so the blur is a
 * tiny copy scaled up, which every browser does the same way and which prints soft either way.
 */
function drawBlurred(ctx, poster, w, h) {
  const small = document.createElement('canvas');
  small.width = 32; small.height = Math.max(1, Math.round(32 * (h / w)));
  const sc = small.getContext('2d');
  const s = Math.max(small.width / poster.width, small.height / poster.height) * 1.2;
  sc.drawImage(poster, (small.width - poster.width * s) / 2, (small.height - poster.height * s) / 2, poster.width * s, poster.height * s);
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(small, 0, 0, w, h);
  ctx.fillStyle = 'rgba(0,0,0,.35)';
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

function drawBrackets(ctx, x0, y0, x1, y1, len, t) {
  ctx.fillStyle = 'rgba(255,255,255,.35)';
  [[x0, y0, len, t], [x0, y0, t, len], [x1 - len, y0, len, t], [x1 - t, y0, t, len],
    [x0, y1 - t, len, t], [x0, y1 - len, t, len], [x1 - len, y1 - t, len, t], [x1 - t, y1 - len, t, len]]
    .forEach(([x, y, a, b]) => ctx.fillRect(x, y, a, b));
}

function wrap(ctx, text, maxWidth) {
  const words = (text || '').split(' ');
  const lines = [];
  let cur = '';
  for (const word of words) {
    const test = cur ? `${cur} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && cur) { lines.push(cur); cur = word; }
    else cur = test;
  }
  if (cur) lines.push(cur);
  return lines;
}

/** Wrap to a line limit; words left over become an ellipsis, never a silent cut. */
function clampLines(ctx, text, maxWidth, max) {
  const lines = wrap(ctx, text, maxWidth);
  if (lines.length <= max) return lines;
  const kept = lines.slice(0, max);
  let last = kept[max - 1];
  while (last && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
  kept[max - 1] = `${last.trimEnd()}…`;
  return kept;
}

/**
 * The per-template layout on the handoff's 2550 × 3300 sheet (V2Print). Rep cards use the same sheet scaled
 * to their own size. `Streak` prints on ivory for low ink.
 */
function printLayout(template, poster) {
  const [iw, ih] = poster ? [poster.width, poster.height] : [1000, 1000];
  if (template === 'streak') {
    return {
      bg: '#F5F0E6', ink: '#111111', lockup: 'black', cap: 110, lockX: 200, capTop: 1880, centerLock: false,
      blur: 1700, poster: containIn(iw, ih, 300, 140, 1950, 1420), outline: 8, streak: 1700,
      text: { x: 200, y: 2120, w: 2150, align: 'left', size: 160 }, qr: { x: 200, y: 2640, s: 520, border: '#111111', ink: '#111111' },
    };
  }
  if (template === 'marquee') {
    return {
      bg: '#000000', ink: '#FFFFFF', lockup: 'white', cap: 130, lockX: 200, capTop: 150, centerLock: false,
      blur: 2340, poster: containIn(iw, ih, 200, 380, 2150, 1840), outline: 8, streak: 2300, panel: 2340,
      text: { x: 200, y: 2400, w: 2150, align: 'left', size: 130 }, qr: { x: 200, y: 2830, s: 400, ink: '#FFFFFF' },
    };
  }
  const box = containIn(iw, ih, 525, 520, 1500, 1320);
  return {
    bg: '#000000', ink: '#FFFFFF', lockup: 'white', cap: 150, lockX: 1275 - 3.125 * 150, capTop: 200,
    ring: box, poster: box,
    text: { x: 225, y: 1980, w: 2100, align: 'center', size: 170 }, qr: { x: 300, y: 2560, s: 560, ink: '#FFFFFF' },
    brackets: true, rule: true,
  };
}

/**
 * Compose a print-ready sheet in the chosen template, with the event details and the branded portal QR.
 * All three sizes follow the template. `poster` is an already-loaded image; `scale` draws it smaller for a
 * thumbnail. Returns { blob, dataUrl, fileName }.
 */
export async function generateSharePoster({ title, dateLabel, venue, city, url, code }, sizeKey = 'poster', template = 'ring', { poster = null, scale = 1 } = {}) {
  const { w, h } = POSTER_SIZES[sizeKey] || POSTER_SIZES.poster;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d');
  if (scale !== 1 && ctx.scale) ctx.scale(scale, scale);
  const place = [venue, city].filter(Boolean).join(' · ');
  const link = brandPath(code ? `events/${code}` : '');
  const font = (weight, px) => { ctx.font = `${weight} ${Math.round(px)}px 'JetBrains Mono', monospace`; };

  const landscape = w > h;
  const qrPx = Math.round(landscape ? h * 0.62 : (printLayout(template, null).qr.s * w) / 2550);
  const qrImg = await brandedQrImage(url, Math.max(qrPx, 256), { margin: 10 }).catch(() => null);

  if (landscape) {
    // Business card: QR left, words right, on the template's ground.
    const ivory = template === 'streak';
    const ink = ivory ? '#111111' : '#FFFFFF';
    ctx.fillStyle = ivory ? '#F5F0E6' : '#000000';
    ctx.fillRect(0, 0, w, h);
    const lockup = await loadImage(LOCKUP_SRC[ivory ? 'black' : 'white']).catch(() => null);
    const pad = Math.round(w * 0.06);
    const qrY = (h - qrPx) / 2;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(pad - 8, qrY - 8, qrPx + 16, qrPx + 16);
    if (qrImg) ctx.drawImage(qrImg, pad, qrY, qrPx, qrPx);
    const tx = pad + qrPx + Math.round(w * 0.04);
    const tw = w - tx - pad;
    if (lockup) drawLockup(ctx, lockup, tx, h * 0.14, (w * 0.17) / 6.25);
    ctx.fillStyle = ink;
    ctx.textAlign = 'left';
    font('700', w * 0.038);
    clampLines(ctx, (title || '').toUpperCase(), tw, 3).forEach((line, i) => ctx.fillText(line, tx, Math.round(h * 0.42 + i * w * 0.046)));
    font('400', w * 0.026);
    ctx.fillText('SCAN TO JOIN', tx, Math.round(h * 0.76));
    ctx.fillText(link.slice(0, 34), tx, Math.round(h * 0.86));
    if (template === 'streak') { ctx.fillStyle = partyGradient(ctx, 0, 0, w, 0); ctx.fillRect(0, h - 14, w, 14); }
    if (template === 'ring') { ctx.fillStyle = partyGradient(ctx, 0, 0, w, 0); ctx.fillRect(0, h - 10, w, 10); }
    if (template === 'marquee') { ctx.fillStyle = partyGradient(ctx, 0, 0, 0, h); ctx.fillRect(0, 0, 14, h); }
  } else {
    const L = printLayout(template, poster);
    const [sx, sy] = [w / 2550, h / 3300];
    const fs = sx;
    const lockup = await loadImage(LOCKUP_SRC[L.lockup]).catch(() => null);
    const art = poster ? null : await loadImage('/brand/portal-o.webp').catch(() => null);
    ctx.fillStyle = L.bg;
    ctx.fillRect(0, 0, w, h);

    if (poster && L.blur) { ctx.save(); drawBlurred(ctx, poster, w, L.blur * sy); ctx.restore(); }
    const picture = poster || art;
    if (picture && L.poster) {
      const fit = poster ? L.poster : containIn(1, 1, L.poster.x, L.poster.y, L.poster.w, L.poster.h);
      const box = { x: fit.x * sx, y: fit.y * sy, w: fit.w * sx, h: fit.h * sy };
      const drawn = containIn(picture.width, picture.height, box.x, box.y, box.w, box.h);
      if (L.ring && poster) {
        ctx.fillStyle = partyGradient(ctx, drawn.x, drawn.y, drawn.x + drawn.w, drawn.y + drawn.h);
        ctx.fillRect(drawn.x - 22 * fs, drawn.y - 22 * fs, drawn.w + 44 * fs, drawn.h + 44 * fs);
      }
      if (L.outline && poster) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(drawn.x - L.outline * fs, drawn.y - L.outline * fs, drawn.w + 2 * L.outline * fs, drawn.h + 2 * L.outline * fs);
      }
      ctx.drawImage(picture, drawn.x, drawn.y, drawn.w, drawn.h);
    }
    if (L.panel) { ctx.fillStyle = '#000000'; ctx.fillRect(0, L.panel * sy, w, h - L.panel * sy); }
    if (L.streak) { ctx.fillStyle = partyGradient(ctx, 0, 0, w, 0); ctx.fillRect(0, L.streak * sy, w, 40 * sy); }
    if (lockup) drawLockup(ctx, lockup, L.lockX * sx, L.capTop * sy, L.cap * fs);

    const T = L.text;
    ctx.textAlign = T.align === 'center' ? 'center' : 'left';
    const tx = (T.align === 'center' ? T.x + T.w / 2 : T.x) * sx;
    let y = T.y * sy;
    ctx.fillStyle = L.ink;
    font('700', T.size * fs);
    clampLines(ctx, title || '', T.w * sx, 2).forEach((line) => { y += T.size * fs * 1.04; ctx.fillText(line, tx, y); });
    y += 40 * sy;
    if (dateLabel) { font('700', 76 * fs); y += 76 * fs; ctx.fillText(dateLabel.toUpperCase(), tx, y); y += 40 * sy; }
    if (place) {
      ctx.globalAlpha = 0.72;
      font('500', 64 * fs);
      y += 64 * fs;
      ctx.fillText(place.toUpperCase(), tx, y);
      ctx.globalAlpha = 1;
    }

    const Q = L.qr;
    // A two-line title pushes the block down: the QR moves clear of it and shrinks to stay on the sheet.
    const qx = Q.x * sx;
    const qy = Math.max(Q.y * sy, y + 60 * sy);
    const qs = Math.min(Q.s * fs, h - qy - 110 * sy);
    if (Q.border) { ctx.fillStyle = Q.border; ctx.fillRect(qx - 10 * fs, qy - 10 * fs, qs + 20 * fs, qs + 20 * fs); }
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(qx, qy, qs, qs);
    if (qrImg) ctx.drawImage(qrImg, qx + qs * 0.07, qy + qs * 0.07, qs * 0.86, qs * 0.86);
    ctx.textAlign = 'left';
    ctx.fillStyle = Q.ink;
    const labelX = qx + qs + 90 * fs;
    font('700', 110 * fs);
    ctx.fillText('[ SCAN TO JOIN ]', labelX, qy + qs / 2 - 20 * fs);
    font('700', 64 * fs);
    ctx.fillText(link.toUpperCase(), labelX, qy + qs / 2 + 90 * fs);

    if (L.rule) { ctx.fillStyle = partyGradient(ctx, 0, 0, w, 0); ctx.fillRect(0, h - 30 * sy, w, 30 * sy); }
    if (L.brackets) drawBrackets(ctx, 70 * sx, 70 * sy, 2480 * sx, 3230 * sy, 120 * fs, 10 * fs);
  }

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  const dataUrl = canvas.toDataURL('image/png');
  const fileName = `${(title || 'event').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${sizeKey}-${template}-portal.png`;
  return { blob, dataUrl, fileName };
}
